import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpenCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const examFormats = {
  NEET: '45 MCQ per subject (+4 / -1)',
  'JEE Mains': '20 MCQ + 5 Numerical per subject (+4 / -1)',
  'JEE Advanced': '6 MCQ Single (+3/-1), 3 Paragraph (+3/-1), 6 Multi (+4/0), 3 Numerical (+4/-1) per subject'
};

const questionTypeOptions = [
  { value: 'mcq_single', label: 'MCQ (Single Correct)' },
  { value: 'numerical', label: 'Numerical' },
  { value: 'mcq_multi', label: 'MCQ (Multi Correct)' },
  { value: 'paragraph', label: 'Paragraph Type' }
];

const PracticeTestSetup = () => {
  const navigate = useNavigate();
  const [syllabus, setSyllabus] = useState(null);
  const [overallAnalysis, setOverallAnalysis] = useState(null);
  const [exam, setExam] = useState('JEE Mains');
  const [mode, setMode] = useState('part');
  const [level, setLevel] = useState('Medium');
  const [subject, setSubject] = useState('');
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [questionTypes, setQuestionTypes] = useState(['mcq_single']);
  const [usePreviousAnalysis, setUsePreviousAnalysis] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchSetupData();
  }, []);

  useEffect(() => {
    if (syllabus) {
      const subjects = Object.keys(syllabus[exam] || {});
      setSubject(subjects[0] || '');
    }
  }, [exam, syllabus]);

  useEffect(() => {
    if (!syllabus || !subject) return;
    const chapters = Object.keys(syllabus[exam]?.[subject] || {});
    setSelectedChapters(chapters.length ? [chapters[0]] : []);
  }, [subject, syllabus, exam]);

  useEffect(() => {
    if (!syllabus || selectedChapters.length === 0) return;
    const topics = selectedChapters.flatMap((ch) => syllabus[exam]?.[subject]?.[ch] || []);
    setSelectedTopics((prev) => {
      const filtered = prev.filter((topic) => topics.includes(topic));
      if (filtered.length) return filtered;
      return topics.length ? [topics[0]] : [];
    });
  }, [selectedChapters, syllabus, exam, subject]);

  const fetchSetupData = async () => {
    try {
      const [syllabusRes, overallRes] = await Promise.all([
        axios.get(`${API}/syllabus`),
        axios.get(`${API}/overall-analysis`).catch(() => null)
      ]);
      setSyllabus(syllabusRes.data);
      setOverallAnalysis(overallRes?.data || null);
    } catch (error) {
      toast.error('Failed to load syllabus');
    } finally {
      setLoading(false);
    }
  };

  const subjectOptions = useMemo(() => Object.keys(syllabus?.[exam] || {}), [syllabus, exam]);
  const chapterOptions = useMemo(() => Object.keys(syllabus?.[exam]?.[subject] || {}), [syllabus, exam, subject]);
  const topicOptions = useMemo(() => {
    const topics = selectedChapters.flatMap((ch) => syllabus?.[exam]?.[subject]?.[ch] || []);
    return Array.from(new Set(topics));
  }, [syllabus, exam, subject, selectedChapters]);

  const handleToggleQuestionType = (value) => {
    setQuestionTypes((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const handleCreateTest = async () => {
    if (mode === 'part' && !usePreviousAnalysis && (!subject || selectedChapters.length === 0 || selectedTopics.length === 0)) {
      toast.error('Please select subject, chapter(s), and topic(s)');
      return;
    }
    if (mode === 'part' && questionTypes.length === 0) {
      toast.error('Select at least one question type');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        exam,
        mode,
        level: mode === 'part' ? level : 'Standard',
        question_count: mode === 'part' ? Number(questionCount) : null,
        subject: mode === 'part' && !usePreviousAnalysis ? subject : null,
        chapters: mode === 'part' && !usePreviousAnalysis ? selectedChapters : null,
        topics: mode === 'part' && !usePreviousAnalysis ? selectedTopics : null,
        question_types: mode === 'part' ? questionTypes : null,
        use_previous_analysis: usePreviousAnalysis
      };
      const response = await axios.post(`${API}/practice-tests/generate-async`, payload);
      navigate(`/practice/generating/${response.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="practice-setup-loading">
        <div className="text-lg text-slate-600">Loading test setup...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="practice-test-setup-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Create Practice Test</h1>
        <p className="text-lg text-slate-600">Choose your exam, focus areas, and generate a smart practice test.</p>
      </div>

      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpenCheck className="w-6 h-6 text-violet-600" />
            Test Configuration
          </CardTitle>
          <CardDescription>Generate a part test or a full-length paper</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Exam</Label>
              <Select value={exam} onValueChange={setExam}>
                <SelectTrigger className="relative z-10" data-testid="practice-exam-select">
                  <SelectValue placeholder="Select exam" />
                </SelectTrigger>
                <SelectContent>
                  {['JEE Mains', 'JEE Advanced', 'NEET'].map((item) => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="relative z-10" data-testid="practice-mode-select">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="part">Part Test (custom)</SelectItem>
                  <SelectItem value="full">Full Test (exam standard)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="practice-exam-format">
            {examFormats[exam]}
          </div>

          {mode === 'part' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={usePreviousAnalysis}
                  onCheckedChange={(checked) => setUsePreviousAnalysis(Boolean(checked))}
                  data-testid="practice-use-analysis-checkbox"
                />
                <span className="text-sm text-slate-700">Use my previous performance analysis</span>
                {usePreviousAnalysis && !overallAnalysis && (
                  <span className="text-xs text-amber-600">No overall analysis found yet.</span>
                )}
              </div>

              {!usePreviousAnalysis && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select value={subject} onValueChange={setSubject}>
                      <SelectTrigger className="relative z-10" data-testid="practice-subject-select">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjectOptions.map((item) => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Chapters</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedChapters(chapterOptions)}
                        data-testid="practice-select-all-chapters"
                      >
                        Select All
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="practice-chapter-list">
                      {chapterOptions.map((item) => (
                        <label key={item} className="flex items-center gap-2 text-sm text-slate-700">
                          <Checkbox
                            checked={selectedChapters.includes(item)}
                            onCheckedChange={() =>
                              setSelectedChapters((prev) =>
                                prev.includes(item) ? prev.filter((ch) => ch !== item) : [...prev, item]
                              )
                            }
                            data-testid={`practice-chapter-${item}`}
                          />
                          {item}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Topics</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTopics(topicOptions)}
                        data-testid="practice-select-all-topics"
                      >
                        Select All
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="practice-topic-list">
                      {topicOptions.map((item) => (
                        <label key={item} className="flex items-center gap-2 text-sm text-slate-700">
                          <Checkbox
                            checked={selectedTopics.includes(item)}
                            onCheckedChange={() =>
                              setSelectedTopics((prev) =>
                                prev.includes(item) ? prev.filter((topic) => topic !== item) : [...prev, item]
                              )
                            }
                            data-testid={`practice-topic-${item}`}
                          />
                          {item}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Difficulty Level</Label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger className="relative z-10" data-testid="practice-level-select">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Easy', 'Medium', 'Hard'].map((item) => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Questions</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={questionCount}
                    onChange={(e) => setQuestionCount(e.target.value)}
                    data-testid="practice-question-count-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Question Types</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="practice-question-types">
                  {questionTypeOptions.map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox
                        checked={questionTypes.includes(option.value)}
                        onCheckedChange={() => handleToggleQuestionType(option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === 'full' && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm text-violet-700" data-testid="practice-full-info">
              Full test will follow the official exam pattern with standard difficulty and subject-wise distribution.
            </div>
          )}

          <Button
            onClick={handleCreateTest}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 font-semibold"
            disabled={creating}
            data-testid="practice-create-test-button"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {creating ? 'Queuing your test...' : 'Generate Practice Test'}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            Generation runs in the background — you can keep using the app and we&apos;ll notify you the moment your
            paper is ready.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PracticeTestSetup;